import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'

const SEEN_KEY = 'facebul-splash-seen'
const HOLD_MS = 5_200 // the clip runs ~5.1s
const FADE_MS = 450
const AUTOPLAY_GRACE_MS = 900

/// Boot splash: the animated Facebul mark plays once per browser session, then fades away.
///
/// Dismissal is owned by a single timer. We deliberately never close on the video's
/// "ended"/"error" events or a rejected play() promise — under StrictMode the first play() is
/// aborted by the remount and rejects, which used to close the splash the instant it appeared.
///
/// Autoplay can also simply be refused (Safari "Auto-Play: Never", low-power mode). We cannot
/// force it, so if the clip has not started shortly after mount we swap in an animated SVG of the
/// same mark. That way the splash is never a frozen poster.
export function Splash() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false
    if (sessionStorage.getItem(SEEN_KEY)) return false
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  const [leaving, setLeaving] = useState(false)
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [fallback, setFallback] = useState(false)

  useEffect(() => {
    if (!show) return
    sessionStorage.setItem(SEEN_KEY, '1')
    document.body.style.overflow = 'hidden'

    void videoRef.current?.play().catch(() => {})

    const grace = window.setTimeout(() => {
      const video = videoRef.current
      if (!video || video.paused || video.currentTime === 0) setFallback(true)
    }, AUTOPLAY_GRACE_MS)
    const timer = window.setTimeout(() => setLeaving(true), HOLD_MS)
    return () => { window.clearTimeout(grace); window.clearTimeout(timer); document.body.style.overflow = '' }
  }, [show])

  useEffect(() => {
    if (!leaving) return
    const timer = window.setTimeout(() => setShow(false), FADE_MS)
    return () => window.clearTimeout(timer)
  }, [leaving])

  if (!show) return null
  return <div
    role="presentation"
    onClick={() => setLeaving(true)}
    className={cn(
      // Pure black matches the clip's own background so the video edge is invisible.
      'fixed inset-0 z-[200] grid cursor-pointer place-items-center bg-black transition-opacity duration-[450ms]',
      leaving ? 'pointer-events-none opacity-0' : 'opacity-100',
    )}
  >
    {!fallback && <video
      ref={videoRef}
      src="/media/facebul-splash.mp4"
      muted
      playsInline
      autoPlay
      preload="auto"
      aria-hidden="true"
      onPlaying={() => setVideoPlaying(true)}
      className={cn('size-56 object-contain transition-opacity sm:size-72', videoPlaying ? 'opacity-100' : 'opacity-0')}
    />}
    {(fallback || !videoPlaying) && <SplashMark className={cn(fallback ? '' : 'absolute')} />}
    <span className="sr-only">Facebul</span>
  </div>
}

/// The mark, drawn in with a scanning pulse — the always-works stand-in for the clip.
function SplashMark({ className }: { className?: string }) {
  return <svg viewBox="0 0 64 64" aria-hidden="true" className={cn('size-56 sm:size-72', className)}>
    <style>{`
      @keyframes fb-draw { to { stroke-dashoffset: 0 } }
      @keyframes fb-node { 0%,100% { opacity:.35 } 50% { opacity:1 } }
      @keyframes fb-frame { 0%,100% { opacity:.55 } 50% { opacity:1 } }
      .fb-frame { animation: fb-frame 1.8s ease-in-out infinite }
      .fb-edge { stroke-dasharray: 160; stroke-dashoffset: 160; animation: fb-draw 2.2s ease-out forwards }
      .fb-node { animation: fb-node 1.6s ease-in-out infinite }
      .fb-smile { stroke-dasharray: 40; stroke-dashoffset: 40; animation: fb-draw 1.2s ease-out 1.4s forwards }
    `}</style>
    <defs>
      <linearGradient id="fb-splash-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#38bdf8" />
        <stop offset="0.5" stopColor="#6366f1" />
        <stop offset="1" stopColor="#2563eb" />
      </linearGradient>
    </defs>
    <g stroke="url(#fb-splash-grad)" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <g className="fb-frame" strokeWidth={5}>
        <path d="M20 6h-8a6 6 0 0 0-6 6v8" />
        <path d="M44 6h8a6 6 0 0 1 6 6v8" />
        <path d="M6 44v8a6 6 0 0 0 6 6h8" />
        <path d="M58 44v8a6 6 0 0 1-6 6h-8" />
      </g>
      <path className="fb-edge" strokeWidth={1.3} d="M30 16 21 22M30 16l4 9M21 22l13 3M21 22l3 8M34 25l-10 5M34 25l2 7M24 30l-6 7M24 30l5 7M18 37h11M29 37l7-5M18 37l5 8M29 37l-6 8M23 45l8 6M29 37l2 14" />
      <path className="fb-smile" strokeWidth={3.6} d="M33 42q7 9 13-4" />
    </g>
    <g className="fb-node" fill="url(#fb-splash-grad)">
      <circle cx="30" cy="16" r="1.9" />
      <circle cx="21" cy="22" r="1.9" />
      <circle cx="34" cy="25" r="1.9" />
      <circle cx="24" cy="30" r="1.9" />
      <circle cx="18" cy="37" r="1.9" />
      <circle cx="29" cy="37" r="1.9" />
      <circle cx="36" cy="32" r="1.9" />
      <circle cx="23" cy="45" r="1.9" />
      <circle cx="31" cy="51" r="1.9" />
      <circle cx="43" cy="26" r="3.4" />
    </g>
  </svg>
}
